import Seo from "@/components/Seo";
import { Reveal, GoldLine } from "@/components/Reveal";
import { MapPin, Phone, MessageCircle, Clock, Mail } from "lucide-react";
import {
    ADDRESS, PHONE_DISPLAY, PHONE_TEL, HOURS, EMAIL, MAP_EMBED,
    DIRECTIONS_URL, waLink, WA_MESSAGES, BRAND_FULL,
} from "@/config/site";
import { openBooking } from "@/lib/api";

const Row = ({ icon: Icon, children, testId }) => (
    <div className="flex items-start gap-4 py-4 border-b border-gold/10" data-testid={testId}>
        <Icon size={17} className="text-gold mt-0.5 shrink-0" strokeWidth={1.5} />
        <div className="text-sm text-ivory/90 leading-relaxed">{children}</div>
    </div>
);

const Visit = () => (
    <main className="pt-32 sm:pt-40 pb-24 min-h-screen">
        <Seo
            title="Visit BSK Jewellers — Jewellery Showroom in Rani Ka Bagh, Amritsar"
            siteName="BSK Jewellers"
            description={`Visit BSK Jewellers (${BRAND_FULL}), ${ADDRESS}. Call or WhatsApp ${PHONE_DISPLAY}, get directions and book a private showroom appointment.`}
            image="/images/chain-gold.jpg"
            jsonLd={{
                "@context": "https://schema.org",
                "@type": "JewelryStore",
                name: "BSK Jewellers",
                alternateName: BRAND_FULL,
                address: { "@type": "PostalAddress", streetAddress: "Rani Ka Bagh", addressLocality: "Amritsar", addressRegion: "Punjab", addressCountry: "IN" },
                telephone: PHONE_DISPLAY,
            }}
        />
        <div className="max-w-[1300px] mx-auto px-5 sm:px-8">
            <Reveal>
                <span className="eyebrow">The Showroom</span>
                <h1 className="font-serif text-4xl sm:text-6xl leading-[1.05] mt-5">
                    Visit <span className="italic gold-text">BSK Jewellers</span>
                </h1>
                <GoldLine delay={0.15} className="w-20 my-7" />
            </Reveal>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mt-6">
                <div>
                    <Reveal>
                        <p className="font-serif text-2xl text-ivory">{BRAND_FULL}</p>
                        <div className="mt-6">
                            <Row icon={MapPin} testId="visit-address">{ADDRESS}</Row>
                            <Row icon={Phone} testId="visit-phone-row">
                                <a href={`tel:${PHONE_TEL}`} className="hover:text-gold transition-colors" data-testid="visit-call-link">{PHONE_DISPLAY}</a>
                            </Row>
                            <Row icon={MessageCircle} testId="visit-whatsapp-row">
                                <a href={waLink(WA_MESSAGES.general)} target="_blank" rel="noreferrer" className="hover:text-gold transition-colors" data-testid="visit-whatsapp-link">WhatsApp {PHONE_DISPLAY}</a>
                            </Row>
                            <Row icon={Mail} testId="visit-email-row">{EMAIL}</Row>
                            <Row icon={Clock} testId="visit-hours-row">
                                {HOURS}
                                <span className="block text-[11px] text-sand/70 mt-1">Please call to confirm timings before your visit.</span>
                            </Row>
                        </div>
                        <div className="flex flex-wrap gap-4 mt-9">
                            <a href={DIRECTIONS_URL} target="_blank" rel="noreferrer" data-testid="visit-directions-btn" className="bg-gold text-obsidian text-[11px] tracking-[0.25em] uppercase px-8 py-4 hover:bg-gold-bright transition-colors">
                                Get Directions →
                            </a>
                            <button onClick={() => openBooking()} data-testid="visit-appointment-btn" className="border border-gold/50 text-gold text-[11px] tracking-[0.25em] uppercase px-8 py-4 hover:bg-gold hover:text-obsidian transition-colors">
                                Book Appointment
                            </button>
                        </div>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <div className="mt-12 border border-gold/15 bg-charcoal/40 p-7">
                            <p className="eyebrow !text-[10px] mb-3">A Note for NRI Families</p>
                            <p className="text-sm text-sand leading-relaxed">
                                Visiting Punjab from abroad? Tell us your dates and we will reserve a private
                                consultation so your jewellery shopping fits perfectly around your trip.
                            </p>
                        </div>
                    </Reveal>
                </div>
                <Reveal delay={0.1}>
                    <div className="relative border border-gold/25 p-2 bg-obsidian h-full min-h-[420px]" data-testid="visit-map-card">
                        <div className="absolute -top-px -left-px w-8 h-8 border-t-2 border-l-2 border-gold z-10" />
                        <div className="absolute -bottom-px -right-px w-8 h-8 border-b-2 border-r-2 border-gold z-10" />
                        <iframe
                            title="BSK Jewellers — Rani Ka Bagh, Amritsar on Google Maps"
                            src={MAP_EMBED}
                            className="w-full h-full min-h-[400px] grayscale invert-[0.92] contrast-[0.9] opacity-90"
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            data-testid="visit-map-embed"
                        />
                    </div>
                </Reveal>
            </div>
        </div>
    </main>
);

export default Visit;
